/**
 * DEX order reconciliation (Activity surface).
 *
 * The in-flight polling in `useDexBridge` only tracks the CURRENT order and stops on unmount /
 * navigation. So a cross-chain order that fulfilled after the user left the page would otherwise
 * show "In progress" forever in Activity. On mount and on an interval, BridgePage re-polls each
 * persisted, non-terminal DEX order's status and marks it complete/failed once the backend reports
 * a terminal state.
 *
 * This module extracts that per-cycle reconcile so it can be unit-tested without mounting the page
 * or driving a real `setInterval`. Behaviour is intentionally identical to the previous inline loop,
 * PLUS the 404 stop rule below (issue #64).
 *
 * Issue #64 — stop polling phantom/unknown orders:
 *   `GET /api/dex/order/:id` returns 404 when the backend has no such order — a same-chain deBridge
 *   swap (order_id is NULL, dex.ts:2134) that lost its terminal state, or a genuinely unknown
 *   transferId (e.g. the beta DB was reset). Such an order will NEVER become known, so re-polling it
 *   every cycle is futile and hammers the endpoint indefinitely. On a 404 we mark the stored order
 *   terminally `failed` so it drops out of the active set next cycle and stops being polled.
 *
 *   This never fails a LIVE cross-chain order: those are persisted WITH their DLN order_id
 *   (dex.ts:2664/2686), so the status endpoint returns 200 for them, not 404. Transient failures
 *   (5xx, network) carry no 404 status and are left as-is to retry. Display-only: no money-path,
 *   no re-submission — a 404 order is simply no longer tracked.
 */

import { getAllActiveDexOrders, updateDexOrder, type StoredDexOrder } from './dexOrderStorage'
import { isAtomicSameChain } from './sameChainGate'
import { isReceiptUnknownMessage } from './errors'
import type { ApiResponse } from '@/types'
import type { DexOrderStatus } from '@/services/api'

/** Orders in these states are settled — never re-poll them. */
const TERMINAL: ReadonlySet<StoredDexOrder['orderStatus']> = new Set(['complete', 'failed'])

/** User-facing note stamped on an order the backend no longer knows (404). Display-only. */
export const ORDER_NOT_FOUND_MESSAGE = 'Order not found on the server — no longer tracked.'

/**
 * DEV-375 finding #6 — how many CONSECUTIVE 404s a receipt-unknown order tolerates before this
 * module stops selecting it for re-polling. A Jupiter same-chain transferId 404s PERMANENTLY (see
 * the carve-out below), so without a stop rule finding #3's 30-day retention (up from the pre-#3
 * 24h prune) turns a bounded futile poll into a ~30x longer one against the same endpoints this repo
 * pinned an RPC ordering for after rate-limiting once made `claimableBalance` silently return 0
 * (`wagmi.ts:97-100`). 3 is deliberately small: this 404 is never transient-then-recovering the way
 * a genuine 5xx might be — the row's table membership does not change — so there is no benefit to
 * waiting longer, only more requests.
 */
const RECEIPT_UNKNOWN_POLL_ABANDON_AFTER = 3

/**
 * The subset of persisted orders worth re-polling this cycle: non-terminal orders, PLUS COMPLETED
 * cross-chain orders still missing their destination tx hash (a one-time backfill so they gain their
 * "Delivering to {chain}" explorer link). Same-chain terminal orders have no separate dest leg, so
 * they are left alone. Terminal orders are otherwise excluded — this is the "(b) terminal status"
 * stop condition: once an order is complete/failed it is no longer polled.
 *
 * The backfill is gated on `'complete'` specifically (not any terminal): a `'failed'` cross-chain
 * order was never filled, so it has no dest tx to backfill — and, critically, gating on `'complete'`
 * is what lets the 404 → `'failed'` stop rule actually terminate. Were `'failed'` cross-chain orders
 * (which always lack a destTxHash) re-admitted here, a phantom order would be re-polled every cycle
 * forever despite being marked failed.
 */
export function selectReconcilableOrders(orders: StoredDexOrder[]): StoredDexOrder[] {
  return orders.filter(o =>
    o.transferId && !o.pollingAbandoned && (!TERMINAL.has(o.orderStatus) ||
      // #614 — keyed on the ATOMIC predicate, not chain equality: a same-chain deposit-flow order
      // (Tron TRX↔USDT via 1Click) has a separate fill tx that must be backfilled like any cross-chain
      // order; only an atomic same-chain swap (deBridge/Jupiter) is done when 'complete'.
      (o.orderStatus === 'complete' && !isAtomicSameChain(o.sourceChain, o.destinationChain, o.provider) && !o.destTxHash)))
}

export interface ReconcileDeps {
  /** Status fetcher — injected so tests can mock without a real network. */
  getOrderStatus: (transferId: string) => Promise<ApiResponse<DexOrderStatus>>
}

/**
 * Run one reconcile pass over the persisted DEX orders. Returns `true` if any stored order changed
 * (so the caller can refresh its view). Pure w.r.t. React — safe to call from an effect or a test.
 */
export async function reconcileDexOrders(deps: ReconcileDeps): Promise<boolean> {
  const active = selectReconcilableOrders(getAllActiveDexOrders())
  if (active.length === 0) return false

  let changed = false
  for (const o of active) {
    try {
      const res = await deps.getOrderStatus(o.transferId)

      if (!res.success || !res.data) {
        // (a) 404 → the backend has no such order and never will → stop polling it forever by
        // marking it terminally failed. Other failures (transient 5xx / network, no 404 status)
        // are left untouched to retry next cycle.
        //
        // DEV-375: EXCEPT a receipt-unknown order. A Jupiter same-chain transferId 404s on this
        // endpoint (it's inserted into `transfers`, not `near1click_orders`/`debridge_orders` —
        // the only tables this route reads), so within one tick this rule would overwrite I10's
        // stored message — the one carrying the tx hash and "don't submit it twice" — with
        // ORDER_NOT_FOUND_MESSAGE and terminalise an order that may still have succeeded. Skip the
        // rewrite entirely for such an order: it stays non-terminal so the background fill (or the
        // user, via the preserved hash) can still resolve it. This is the ONLY carve-out — every
        // other error class still hits the rule below exactly as before.
        if (res.status === 404) {
          if (!isReceiptUnknownMessage(o.error)) {
            updateDexOrder(o.transferId, {
              orderStatus: 'failed',
              error: ORDER_NOT_FOUND_MESSAGE,
              // #261 F9 — this order died while being POLLED (it is only reachable from the
              // non-terminal set, which it entered after its source tx was broadcast), so the progress
              // view reds the tracking row rather than all three.
              failedStep: 'polling',
            })
            changed = true
          } else {
            // DEV-375 finding #6 — the carve-out above stops finding #3's 30-day-retained
            // receipt-unknown row from being terminalised, but a permanently-404ing transferId
            // (Jupiter same-chain) would otherwise be re-polled every cycle FOREVER — no fill can
            // ever resolve it (comment above). Count CONSECUTIVE 404s and, once tracking is futile,
            // stop SELECTING the order (`selectReconcilableOrders` excludes `pollingAbandoned`)
            // without writing 'failed' and without discarding the hash or the warning — the row
            // stays exactly as I10 left it, just no longer polled.
            const notFoundCount = (o.notFoundCount ?? 0) + 1
            const patch: Partial<StoredDexOrder> = { notFoundCount }
            if (notFoundCount >= RECEIPT_UNKNOWN_POLL_ABANDON_AFTER) patch.pollingAbandoned = true
            updateDexOrder(o.transferId, patch)
            changed = true
          }
        }
        continue
      }

      const s = res.data.state?.toLowerCase()
      // Capture the source/dest tx hashes the status reports (authoritative; address-independent) so
      // the explorer links render for both legs even when transfer-history doesn't return this transfer.
      const destTx = res.data.fulfilledDstEventMetadata?.transactionHash
      const srcTx = res.data.createdSrcEventMetadata?.transactionHash
      const patch: Partial<StoredDexOrder> = {}
      // GATE-4 cycle 3 — this branch is reached only on a successful (non-404) status response, so a
      // healthy poll resets the CONSECUTIVE 404 counter the doc comment above already claims. Without
      // this, the counter is cumulative across the order's whole lifetime: two transient 404 epochs
      // separated by healthy polls would accumulate to the abandon threshold even though neither
      // epoch was long enough on its own — abandoning a still-live, still-answering order.
      if (o.notFoundCount) patch.notFoundCount = 0
      if (s === 'fulfilled' || s === 'claimedunlock' || s === 'sentunlock') {
        patch.orderStatus = 'complete'
      } else if (s === 'ordercancelled' || s === 'claimedordercancel') {
        patch.orderStatus = 'failed'
        // #261 F9 — INSIDE this branch, never above it. `patch` is only written when it is non-empty
        // (the `Object.keys(patch).length > 0` gate below); an unconditional assignment would make
        // that always true and rewrite every tracked order on every 5s cycle, which is both a
        // pointless localStorage write and a spurious `changed` → view refresh (falsifier F-F).
        patch.failedStep = 'polling'
      }
      if (destTx && !o.destTxHash) patch.destTxHash = destTx
      if (srcTx && !o.sourceTxHash) patch.sourceTxHash = srcTx
      if (Object.keys(patch).length > 0) {
        updateDexOrder(o.transferId, patch)
        changed = true
      }
    } catch {
      // Transport error (fetch threw) — leave as-is and retry next cycle.
    }
  }
  return changed
}
